import { Link, useRouterState } from "@tanstack/react-router";
import { LogOut } from "lucide-react";
import type * as React from "react";
import { PlaidReconnectBanner } from "@/components/plaid-reconnect-banner";
import { Button } from "@/components/ui/button";
import { isSignedIn, signOut } from "@/lib/session";
import { useOrganization } from "@/lib/use-organization";
import { cn } from "@/lib/utils";

export function PageShell({
  children,
  showSignOut = false,
  hasStickyFooter = false,
  className,
}: {
  children: React.ReactNode;
  showSignOut?: boolean;
  hasStickyFooter?: boolean;
  className?: string;
}) {
  const organization = useOrganization();
  const authed = isSignedIn();
  // /plaid-links is where an OAuth bank returns to finish a Link session;
  // the page resumes it itself.
  const onPlaidReturn = useRouterState({
    select: (state) => state.location.pathname === "/plaid-links",
  });

  function handleSignOut() {
    signOut();
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border/70 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
        <div className="mx-auto flex h-14 w-full max-w-xl items-center justify-between px-4">
          <Link
            to={authed ? "/steps" : "/"}
            className="flex items-center gap-2.5 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          >
            {organization.logo_url ? (
              <img
                src={organization.logo_url}
                alt={organization.name}
                className="h-7 w-auto"
              />
            ) : (
              <span className="font-heading text-sm font-semibold text-foreground">
                {organization.name}
              </span>
            )}
          </Link>
          {(showSignOut || authed) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSignOut}
              data-icon="inline-start"
            >
              <LogOut className="size-3.5" />
              Sign out
            </Button>
          )}
        </div>
      </header>

      <main
        className={cn(
          "mx-auto w-full max-w-xl px-4 pt-6 pb-10",
          hasStickyFooter && "pb-28",
          className,
        )}
      >
        {authed && !onPlaidReturn && <PlaidReconnectBanner />}
        {children}
      </main>
    </div>
  );
}
